import { FormInput } from '@common/components/forms';
import { CancelAndEditActions } from '@common/components/forms/CancelAndEditActions';
import { PageCard, PageCardProps } from '@common/components/layout';
import { YesNoConfirmModal } from '@common/components/modals';
import { SuspenseButton } from '@common/components/utility/SuspenseButton';
import { useUnsavedChangesWarning } from '@common/hooks';
import { HospitalModel, IIvlsDeviceModel } from '@common/models';
import { HospitalService } from '@common/services';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { IvlsDeviceTable } from './IvlsDeviceTable';
import { LocationDiagnosticDisplay } from './LocationDiagnosticDisplay';

type IProps = {
    hospital: HospitalModel;
    onUpdateHospital: (data: HospitalModel) => void;
    refreshIvlsDevices: (hospitalId: number) => void;
    ivlsDevices: IIvlsDeviceModel[] | undefined
} & PageCardProps

export const LocationDiagnosticDetail = observer(({ hospital, onUpdateHospital, refreshIvlsDevices, ivlsDevices, ...cardProps }: IProps) => {
    const {
        handleSubmit, control, reset, formState: { isDirty }
    } = useFormContext<Omit<HospitalModel, "defaultScheduleTemplate">>() //<- omit defaultScheduleTemplate because it causes a circular reference.

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [diagnosticDetailDirty, setDiagnosticDetailDirty] = React.useState<boolean>(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);

    const cancelingEditing = () => {
        if (diagnosticDetailDirty)
            setShowCancelConfirm(true);
        else
            cancelEditing();
    }

    const cancelEditing = () => {
        setIsEditing(false);
        setShowCancelConfirm(false);
        reset()
    }
    const unsavedChangesPrompt = useUnsavedChangesWarning(diagnosticDetailDirty);
    useEffect(() => {
        if (setDiagnosticDetailDirty) {
            setDiagnosticDetailDirty(isDirty);
        }

    }, [isDirty, setDiagnosticDetailDirty])

    const formId = 'diagnosticDetailsEditForm'
    const disable = false;



    useEffect(() => {
        if (setDiagnosticDetailDirty) {
            setDiagnosticDetailDirty(isDirty);
        }

    }, [isDirty, setDiagnosticDetailDirty])


    const [defaultIvlsDevice, setDefaultIvlsDevice] = React.useState(
        (ivlsDevices)
            ? _.first(_.orderBy(ivlsDevices, "lastPolledCloudTime", "desc"))
            : undefined
    );

    const [lastPolledCloudTime, setLastPolledCloudTime] = React.useState(
        (defaultIvlsDevice?.lastPolledCloudTime)
            ? defaultIvlsDevice?.lastPolledCloudTime.toLocaleString(DateTime.DATETIME_SHORT)
            : ''
    );

    React.useEffect(() => {
        setDefaultIvlsDevice((ivlsDevices)
            ? _.first(_.orderBy(ivlsDevices, "lastPolledCloudTime", "desc"))
            : undefined);

        setLastPolledCloudTime((defaultIvlsDevice?.lastPolledCloudTime)
            ? defaultIvlsDevice?.lastPolledCloudTime.toLocaleString(DateTime.DATETIME_SHORT)
            : ''
        );

    }, [ivlsDevices, defaultIvlsDevice?.lastPolledCloudTime]);

    return (
        <>
            {unsavedChangesPrompt}
            <YesNoConfirmModal
                visible={showCancelConfirm}
                title="Cancel Changes"
                description="You have unsaved changes, you are sure you want to cancel?"
                busy={false}
                cancelButtonText='No'
                onConfirm={() => cancelEditing()}
                onCancel={() => { setIsEditing(true); setShowCancelConfirm(false) }}
            />
            <PageCard title="Hospital Diagnostic Details" actionElement={
                <CancelAndEditActions
                    formId={formId}
                    editText="Edit Details"
                    saveText="Save Details"
                    isEditing={isEditing}
                    saveClicked={(e) => {
                        if (!isEditing) {
                            e?.preventDefault();
                            return false;
                        }
                        return true;
                    }}
                    cancelClicked={cancelingEditing}
                    editClicked={() => {
                        if (!isEditing) {
                            setIsEditing(true);
                        }
                    }}
                />
            } {...cardProps}>
                {isEditing ?
                    <Form id={formId} className="input-form" onSubmit={handleSubmit(onUpdateHospital)}>
                        <Row className="pt-4">
                            <Col md={6}>
                                <FormInput label="Diagnostic Lab Username" fieldName="diagnosticLabUserName" control={control} disabled={disable} />
                            </Col>
                            <Col md={6}>
                                <FormInput label="Diagnostic Lab Password" fieldName="diagnosticLabPassword" control={control} disabled={disable} />
                            </Col>
                        </Row>
                    </Form> :
                    <LocationDiagnosticDisplay hospital={hospital} />
                }
                <br></br>
                <hr />
                <div className={"d-flex flex-fill justify-content-between align-items-top"} >
                    <h2 className='pagecard-header'>Ivls Devices:</h2>
                    {lastPolledCloudTime
                        ? <span className='d-inline-block me-3 text-primary mt-1'> Last Updated Date: {lastPolledCloudTime}</span>
                        : undefined
                    }
                    <div>
                        <SuspenseButton
                            tag={HospitalService.tags.refreshHospitalIvlsDevices}
                            variant="primary"
                            onClick={() => { refreshIvlsDevices(hospital?.hospitalId) }}
                        >
                            Refresh IVLS Devices
                        </SuspenseButton>
                    </div>

                </div>
                <IvlsDeviceTable ivlsDevices={ivlsDevices} />
            </PageCard>
        </>
    )
});
